import { useState, useEffect, useRef } from 'react';
import { TrendingUp, Clock, CheckCircle } from 'lucide-react';

export default function ROICalculator() {
  const [hires, setHires] = useState(50);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const calculateSavings = (value: number) => {
    const baseSavingPerCandidate = 60000;
    const bonusMultiplier = 1 + (Math.floor(value / 10) * 0.1);
    const totalSavings = value * baseSavingPerCandidate * bonusMultiplier;

    return {
      money: Math.round(totalSavings).toLocaleString('en-IN'),
      time: Math.floor(value * 5).toLocaleString('en-IN')
    };
  };

  const savings = calculateSavings(hires);

  return (
    <section ref={sectionRef} id="calculator" className="py-24 bg-transparent border-t border-white/5 relative overflow-hidden">

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className={`glass-dark p-8 md:p-12 rounded-3xl bg-gradient-to-b from-slate-900 to-black border-2 border-emerald-500/30 relative overflow-hidden transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none"></div>

          <div className="text-center mb-12 relative z-10">
            <div className="flex items-center justify-center gap-3 mb-6">
              <picture>
                <source srcSet="/image_40.webp" type="image/webp" />
                <img
                  src="/image.png"
                  alt="GreenTick"
                  width="40"
                  height="40"
                  className="h-8 md:h-10 w-auto"
                />
              </picture>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">ROI Calculator</h2>
            <p className="text-slate-400">
              See the ROI of switching to intelligent AI-powered screening
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center relative z-10">
            <div className="lg:col-span-2 space-y-6">
              <div className="glass-dark p-6 rounded-2xl border border-white/10">
                <label htmlFor="roi-hires-slider" className="block text-sm text-slate-300 mb-6 font-bold">
                  Annual Hires
                </label>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-xs text-slate-400 font-mono">10</span>
                  <input
                    id="roi-hires-slider"
                    type="range"
                    min="10"
                    max="500"
                    value={hires}
                    onChange={(e) => setHires(parseInt(e.target.value))}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  />
                  <span className="text-xs text-slate-400 font-mono">500</span>
                </div>
                <div className="text-center py-6 bg-black/40 rounded-xl border border-emerald-500/30">
                  <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400 font-mono">
                    {hires}
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-widest mt-2">Candidates/Year</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-4">
              <div className="p-8 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-black border-2 border-emerald-500 text-center relative overflow-hidden group">
                <div className="absolute inset-0 bg-emerald-500/5 group-hover:bg-emerald-500/10 transition-colors"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <TrendingUp className="w-6 h-6 text-emerald-400" />
                    <div className="text-sm uppercase tracking-widest text-emerald-400 font-bold">
                      Annual Savings
                    </div>
                  </div>
                  <div className="text-5xl md:text-6xl font-bold text-white mb-2 font-mono tracking-tighter">
                    ₹{savings.money}
                  </div>
                  <div className="text-sm text-slate-400">vs traditional screening</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 rounded-2xl bg-slate-800/50 border border-white/10 text-center">
                  <Clock className="w-8 h-8 text-teal-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white mb-1 font-mono">
                    {savings.time}
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Hours Saved</div>
                </div>

                <div className="p-6 rounded-2xl bg-slate-800/50 border border-white/10 text-center">
                  <CheckCircle className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white mb-1 font-mono">
                    10X
                  </div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Faster Hiring</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <a href="https://calendar.app.google/SxKx5dUS1G1pBYTFA" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-emerald-500/50 transition-all hover:scale-105">
              Book Demo
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
